import { AddressKind, AddressModel, ContactKind, PersonModel, PersonRole } from '@ail/customers-api'
import { VehicleOrderInputModel } from '@ail/orders-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { addDaysToDate, hasTimeElapsed, printDate, zeroTimeZoneDate } from '../../../../helpers/Utils'
import { useGetCustomerPersonsQuery } from '../../../../redux/api/customerApi'
import { usePutVehicleOrderMutation } from '../../../../redux/api/ordersApi'
import { monthNames } from '../../../../types/constants'
import { ICalendarInfo } from '../../../../types/interfaces'
import { GridRow } from '../../../DeliveryAndReturns/Delivery'
import LicencePlateFlag from '../../LicencePlateFlag/LicencePlateFlag'
import { Modal } from '../Modal'
import AddressListItem from './AddressListItem'
import ContactListItem from './ContactListItem'
import Scheduler from './Scheduler/Scheduler'
import './SchedulerModal.scss'
import { DeliveryFormData } from '../../../DeliveryAndReturns/SchedulerModals/CombinedScheduler'
import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner'

export interface SchedulerProps {
  /** Modal is visible */
  visible: boolean
  /** On close button was pressed */
  onClose: () => void
  /** On confirm button was pressed. */
  onConfirm?: (day: number) => void
  /** Data from selected row. */
  dataRow: GridRow
  freeze?: string
  combinedView?: boolean
  onDataUpdate?: (data: DeliveryFormData) => void
}

export const SchedulerModal = (props: SchedulerProps) => {
  const { contract, vehicleOrder, customer, costCenter } = props.dataRow
  const { t } = useTranslation('common')
  const { combinedView, onDataUpdate } = props

  const [updateVehicleOrder] = usePutVehicleOrderMutation()
  const persons = useGetCustomerPersonsQuery({
    customerId: customer?.customer?.id ?? '',
    roles: [PersonRole.Primary, PersonRole.FleetManager, PersonRole.ObjectManager],
  })
  //Calendar
  const [date, setDate] = useState<number>(0)
  const [selectedDay, setSelectedDay] = useState<number | undefined>()
  //Selectors & textarea
  const [selectedContact, setSelectedContact] = useState<PersonModel>()
  const [selectedAddress, setSelectedAddress] = useState<AddressModel>()
  const [selectedTiresAddress, setSelectedTiresAddress] = useState<AddressModel>()
  const [manualContact, setManualContact] = useState<string>()
  const [manualAddress, setManualAddress] = useState<string>()
  const [manualTiresAddress, setManualTiresAddress] = useState<string>()
  //Selector Lists togglers
  const [contactOpen, setContactOpen] = useState(false)
  const [addressOpen, setAddressOpen] = useState(false)
  const [tiresAddressOpen, setTiresAddressOpen] = useState(false)
  //Search
  const [searchString, setSearchString] = useState('')
  const [searchAddressString, setSearchAddressString] = useState('')
  const [tiresAddressSearch, setTiresAddressSearch] = useState('')

  const [dateMissing, setDateMissing] = useState(false)
  const [requiredFieldsMissing, setRequiredFieldsMissing] = useState(false)
  const [noPhoneWarning, setNoPhoneWarning] = useState(false)
  const frozenOrder = !!props.freeze && hasTimeElapsed(props.freeze, 0) && !vehicleOrder?.earliestDelivery
  const [reminderAcceptMissing, setReminderAcceptMissing] = useState(true)
  const [userConfirmation, setUserConfirmation] = useState(false)

  //Auto-fills contact text-area on selection
  useEffect(() => {
    const contact = selectedContact ? getContactDisplayInfo(selectedContact) : ''

    setManualContact(contact)
  }, [selectedContact])

  //Auto-fills address text-area on selection
  useEffect(() => {
    const address = selectedAddress
      ? `${selectedAddress.street}, ${selectedAddress.postalCode}, ${selectedAddress.city}`
      : ''
    setManualAddress(address)
  }, [selectedAddress])

  //Auto-fills tires address text-area on selection
  useEffect(() => {
    const address = selectedTiresAddress
      ? `${selectedTiresAddress.street}, ${selectedTiresAddress.postalCode}, ${selectedTiresAddress.city}`
      : ''
    setManualTiresAddress(address)
  }, [selectedTiresAddress])

  /** FIltered persons based on relevant costCenter (passed from parent). If no costCenter matches, then uses all persons from response   */
  const relevantPersons = useMemo(() => {
    if (!persons.data) return []
    const foundPersons = persons.data.filter((p) => p.unitName === costCenter)
    return foundPersons.length > 0 ? foundPersons : persons.data
  }, [costCenter, persons.data])
  /** FIltered addresses based on relevant persons and address kinds.*/
  const relevantAddresses = useMemo(() => {
    const validKinds = [AddressKind.Delivery, AddressKind.GroupShippingAddress, AddressKind.Primary]
    const streetSet = new Set()
    return relevantPersons?.flatMap((c) => {
      return c.addresses.filter((a) => {
        if (validKinds.includes(a.kind) && !streetSet.has(a.street)) {
          streetSet.add(a.street)
          return true
        }
        return false
      })
    })
  }, [relevantPersons])

  const filteredTiresAddresses = useMemo(() => {
    return relevantAddresses.filter((a) => {
      const searchString = tiresAddressSearch.toLowerCase()
      return (
        a.street?.toLowerCase().includes(searchString) ||
        a.city?.toLowerCase().includes(searchString) ||
        a.country?.toLowerCase().includes(searchString)
      )
    })
  }, [relevantAddresses, tiresAddressSearch])

  const filteredPersons = useMemo(() => {
    return relevantPersons
      .filter((p) => p.displayName?.toLowerCase().includes(searchString.toLowerCase()))
      .sort((a, b) => (a.displayName?.toLowerCase() ?? '').localeCompare(b.displayName?.toLowerCase() ?? ''))
  }, [relevantPersons, searchString])

  const filteredAddresses = useMemo(() => {
    return relevantAddresses.filter((a) => {
      const searchString = searchAddressString.toLowerCase()
      return (
        a.street?.toLowerCase().includes(searchString) ||
        a.city?.toLowerCase().includes(searchString) ||
        a.country?.toLowerCase().includes(searchString)
      )
    })
  }, [relevantAddresses, searchAddressString])

  // Automatically opens selector lists when search string exists
  useEffect(() => {
    if (searchString) {
      setContactOpen(true)
    }
  }, [searchString])
  useEffect(() => {
    if (searchAddressString) {
      setAddressOpen(true)
    }
  }, [searchAddressString])

  // #region Data Aggregation

  const { orderEarliestDate, actualMinDate } = useMemo(() => {
    const now = new Date()
    const orderEarliestDate = new Date(vehicleOrder?.earliestDelivery ?? 0)
    const actualMinDate = orderEarliestDate > addDaysToDate(now, 2) ? orderEarliestDate : addDaysToDate(now, 2)
    setDate(addDaysToDate(actualMinDate, 1).getTime())
    if (orderEarliestDate > now) {
      setSelectedDay(actualMinDate.getTime())
    }
    return { orderEarliestDate, actualMinDate }
  }, [vehicleOrder?.earliestDelivery])

  const calendarInfoToShow: ICalendarInfo = useMemo(() => {
    const vehicle = contract?.vehicle
    const logo = vehicle?.makeName?.toLowerCase().includes('ladestation')
      ? 'dummy'
      : vehicle?.makeName?.replace(/\s+/g, '-').toLowerCase()
    return {
      selectedDay: actualMinDate.getTime(),
      minDate: actualMinDate.getTime(),
      maxDate: addDaysToDate(orderEarliestDate, 46).getTime(),
      model: `${vehicle?.makeName} ${vehicle?.modelName}`.toUpperCase(),
      brandImage: `../../../../BW/${logo?.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.svg`,
      plateNumber: vehicle?.licensePlate ? vehicle.licensePlate.licenseNumber : undefined,
      driverName: vehicle?.driver?.displayName ?? '',
      companyName: customer?.customer?.name ?? '',
    }
  }, [actualMinDate, customer?.customer?.name, orderEarliestDate, contract])
  // #endregion
  // #region Calendar FX
  const selectDay = (day?: number) => {
    if (day) {
      setDate(day)
      setSelectedDay(day)
    } else {
      setSelectedDay(undefined)
      setDate(Date.now())
    }
    setDateMissing(false)
  }

  /**
   * Select next month.
   */
  const plusMonth = () => {
    if (!date) return
    let month = new Date(date).getMonth()
    let year = new Date(date).getFullYear()

    if (month === 11) {
      month = 1
      year++
    } else {
      month++
    }

    setDate(new Date(year, month, 1).getTime())
  }

  /**
   * Select previous month.
   */
  const minusMonth = () => {
    if (!date) return
    let month = new Date(date).getMonth()
    let year = new Date(date).getFullYear()

    if (month === 0) {
      month = 11
      year--
    } else {
      month--
    }

    setDate(new Date(year, month, 1).getTime())
  }
  const requiredDataIsValid = useCallback(() => {
    if (!manualAddress || !manualContact || !manualTiresAddress || (!selectedDay && !frozenOrder)) {
      setRequiredFieldsMissing(true)
      setDateMissing(true)
      return false
    } else return true
  }, [frozenOrder, manualAddress, manualContact, selectedDay, manualTiresAddress])

  const confirm = useCallback(() => {
    if (requiredDataIsValid())
      if (vehicleOrder) {
        const canUpdateOrder = selectedDay || frozenOrder
        if (!canUpdateOrder) return
        const newDate =
          !frozenOrder && selectedDay ? zeroTimeZoneDate(new Date(selectedDay)).toISOString().slice(0, -5) : undefined
        const model: VehicleOrderInputModel = {
          confirmedDelivery: newDate,
          confirmedDeliveryAddress: manualAddress ?? vehicleOrder.confirmedDeliveryAddress,
          confirmedDeliveryContact: manualContact ?? vehicleOrder.confirmedDeliveryContact,
          confirmedTireDeliveryAddress: manualTiresAddress ?? vehicleOrder.confirmedTireDeliveryAddress,
        }

        if (combinedView && onDataUpdate) {
          onDataUpdate({ ...model, userConfirmation: !userConfirmation })
          return
        }
        updateVehicleOrder({
          id: vehicleOrder.id,
          model,
        })
        props.onClose()
      } else {
        if (combinedView && onDataUpdate) return
        props.onClose()
      }
  }, [
    requiredDataIsValid,
    vehicleOrder,
    selectedDay,
    frozenOrder,
    manualAddress,
    manualContact,
    manualTiresAddress,
    combinedView,
    onDataUpdate,
    updateVehicleOrder,
    props,
    userConfirmation,
  ])

  /** Autofilling address selection if any of the validKinds are present in address array of person. 
 Does not clear address value, when contact is reset*/
  function autoFillAddress(person: PersonModel) {
    const validKinds = [AddressKind.Delivery, AddressKind.GroupShippingAddress, AddressKind.Primary]
    const validAddress = person.addresses.find((a) => {
      if (validKinds.includes(a.kind)) return a
    })

    setSelectedAddress(validAddress)
    setSelectedTiresAddress(validAddress)
  }

  /** Displays basic info about person as a string. Contact info can be adjusted based on ContactKind */
  function getContactDisplayInfo(person: PersonModel) {
    const phone = person.contacts.find((c) =>
      [ContactKind.PhoneNumber, ContactKind.MobileNumber].includes(c.kind)
    )?.value
    const email = person.contacts.find((c) => c.kind === ContactKind.Email)?.value
    return [person.displayName, phone, email].filter(Boolean).join(', ')
  }
  // #endregion

  //Frozen order autofill data, if it exists
  useEffect(() => {
    if (vehicleOrder) {
      setManualAddress(vehicleOrder.confirmedDeliveryAddress)
      setManualContact(vehicleOrder.confirmedDeliveryContact)
      setManualTiresAddress(vehicleOrder.confirmedTireDeliveryAddress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #region CombinedView
  const handleConfirmationChange = useCallback(() => {
    if (requiredDataIsValid()) {
      setUserConfirmation(!userConfirmation)
      setReminderAcceptMissing(userConfirmation)
      if (!userConfirmation) confirm()
    }
  }, [requiredDataIsValid, userConfirmation, confirm])
  // #endregion

  // #region UI
  const confirmContainer = (
    <div className="scheduler-confirm-area">
      <div className="transparent-button negative" onClick={props.onClose}>
        <i className="icon-close"></i>
        {t('CLOSE')}
      </div>
      <div className="transparent-button positive" onClick={confirm}>
        <i className="icon-check"></i>
        {t('CONFIRM')}
      </div>
    </div>
  )

  const schedulerContent = (
    <div className="sheduler-content">
      <div>
        <div className="head">
          <div className="title">
            <i className={frozenOrder ? 'icon-marker' : combinedView ? 'icon-garageOut' : 'icon-calendar'}></i>
            <span>{t('DELIVERY_INFO')}</span>
          </div>
        </div>
        <div className="info">
          <div className="first">
            <div className="flex">
              <div>
                <i className="icon-customers"></i>
                {calendarInfoToShow.companyName}
              </div>
              <div>
                <i className="icon-driver"></i>
                {calendarInfoToShow?.driverName}
              </div>
            </div>
            <div>
              <img width="20" src={calendarInfoToShow.brandImage} />
              {calendarInfoToShow.model}
            </div>
          </div>
          <div className="second">
            <div className="user-info">
              {calendarInfoToShow?.plateNumber && (
                <div>
                  {calendarInfoToShow?.plateNumber && (
                    <LicencePlateFlag licenceNumber={calendarInfoToShow?.plateNumber} width="12" height="14" />
                  )}
                </div>
              )}
            </div>
            {/*<div className="icon">
            <i className="icon-delivery"></i>
          </div>*/}
          </div>
        </div>
        <div className="calendarAddress">
          <div className="inputGroup">
            <div className="flex">
              <div>
                <i className="icon-manager" />
                <label>{t('DELIVERY_CONTACT')}</label>
                {noPhoneWarning && (
                  <i className="icon-warning">
                    <span className="tooltip">{t('MISSING_CONTACT_PHONE')}</span>
                  </i>
                )}
              </div>
            </div>
            <div className="contracts-dropdown-filter-ui">
              <div className="search">
                <input
                  disabled={userConfirmation}
                  type="text"
                  list="autocompleteOff"
                  placeholder={t('SEARCH_OR_SELECT')}
                  value={selectedContact ? getContactDisplayInfo(selectedContact) : searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  onClick={() => {
                    setAddressOpen(false)
                    setContactOpen(!contactOpen)
                    setTiresAddressOpen(false)
                  }}
                />
                {selectedContact && (
                  <i
                    className="icon-clear"
                    style={{ marginRight: '15px' }}
                    onClick={() => {
                      setContactOpen(false)
                      setSelectedContact(undefined)
                      setSearchString('')
                    }}
                  ></i>
                )}
                {!selectedContact && persons.isLoading && <LoadingSpinner color="#b82585" size={12} />}
                <i
                  className={`${contactOpen ? 'icon-up' : 'icon-bottom'}`}
                  onClick={() => {
                    setAddressOpen(false)
                    setContactOpen(!contactOpen)
                    setTiresAddressOpen(false)
                  }}
                />
              </div>
              {contactOpen && !!filteredPersons?.length && (
                <div className="search-results">
                  <ul>
                    {filteredPersons?.map((m) => (
                      <ContactListItem
                        person={m}
                        onClick={(val) => {
                          setContactOpen(false)
                          setSelectedContact(m)
                          autoFillAddress(m)
                          setNoPhoneWarning(val)
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <textarea
              disabled={userConfirmation}
              className={`result ${requiredFieldsMissing && !manualContact ? `requiredField` : ''}`}
              placeholder={requiredFieldsMissing && !manualContact ? t('MISSING_DELIVERY_CONTACT') : ''}
              value={manualContact ? manualContact : ''}
              onChange={(e) => setManualContact(e.target.value)}
            />
          </div>
          <div className="inputGroup">
            <div className="flex">
              <div>
                <i className="icon-marker" />
                <label>{t('DELIVERY_ADDRESS')}</label>
              </div>
            </div>
            <div className="contracts-dropdown-filter-ui">
              <div className="search">
                <input
                  disabled={userConfirmation}
                  type="text"
                  list="autocompleteOff"
                  placeholder={t('SEARCH_OR_SELECT')}
                  value={
                    selectedAddress
                      ? `${selectedAddress.street}, ${selectedAddress.postalCode}, ${selectedAddress.city}`
                      : searchAddressString
                  }
                  onChange={(e) => setSearchAddressString(e.target.value)}
                  onClick={() => {
                    setAddressOpen(!addressOpen)
                    setContactOpen(false)
                    setTiresAddressOpen(false)
                  }}
                />
                {selectedAddress && (
                  <i
                    className="icon-clear"
                    style={{ marginRight: '15px' }}
                    onClick={() => {
                      setAddressOpen(false)
                      setSelectedAddress(undefined)
                      setSearchAddressString('')
                    }}
                  ></i>
                )}
                <i
                  className={`${addressOpen ? 'icon-up' : 'icon-bottom'}`}
                  onClick={() => {
                    setAddressOpen(!addressOpen)
                    setContactOpen(false)
                    setTiresAddressOpen(false)
                  }}
                />
              </div>
              {addressOpen && !!relevantAddresses?.length && (
                <div className="search-results">
                  <ul>
                    {filteredAddresses.map((address) => (
                      <AddressListItem
                        address={address}
                        onClick={() => {
                          setAddressOpen(false)
                          setSelectedAddress(address)
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <textarea
              disabled={userConfirmation}
              className={`result ${requiredFieldsMissing && !manualAddress ? `requiredField` : ''}`}
              placeholder={requiredFieldsMissing && !manualAddress ? t('MISSING_DELIVERY_ADDRESS') : ''}
              value={manualAddress ? manualAddress : ''}
              onChange={(e) => setManualAddress(e.target.value)}
            />
          </div>
          <div className="inputGroup">
            <div className="flex">
              <div>
                <i className="icon-tire-storage" />
                <label>{t('TYRE_ADDRESS')}</label>
              </div>
            </div>
            <div className="contracts-dropdown-filter-ui">
              <div className="search">
                <input
                  disabled={userConfirmation}
                  type="text"
                  list="autocompleteOff"
                  placeholder={t('SEARCH_OR_SELECT')}
                  value={
                    selectedTiresAddress
                      ? `${selectedTiresAddress?.street}, ${selectedTiresAddress.postalCode}, ${selectedTiresAddress?.city}`
                      : tiresAddressSearch
                  }
                  onChange={(e) => setTiresAddressSearch(e.target.value)}
                  onClick={() => {
                    setContactOpen(false)
                    setAddressOpen(false)
                    setTiresAddressOpen(!tiresAddressOpen)
                  }}
                />
                {selectedTiresAddress && (
                  <i
                    className="icon-clear"
                    style={{ marginRight: '15px' }}
                    onClick={() => {
                      setTiresAddressOpen(false)
                      setSelectedTiresAddress(undefined)
                      setTiresAddressSearch('')
                    }}
                  ></i>
                )}
                <i
                  className={`${tiresAddressOpen ? 'icon-up' : 'icon-bottom'}`}
                  onClick={() => {
                    setContactOpen(false)
                    setAddressOpen(false)
                    setTiresAddressOpen(!tiresAddressOpen)
                  }}
                />
              </div>
              {tiresAddressOpen && !!relevantAddresses?.length && (
                <div className="search-results">
                  <ul>
                    {filteredTiresAddresses.map((address) => (
                      <AddressListItem
                        address={address}
                        onClick={() => {
                          setTiresAddressOpen(false)
                          setSelectedTiresAddress(address)
                        }}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <textarea
              disabled={userConfirmation}
              className={`result ${requiredFieldsMissing && !manualTiresAddress ? `requiredField` : ''}`}
              placeholder={requiredFieldsMissing && !manualTiresAddress ? t('MISSING_TYRE_ADDRESS') : ''}
              value={manualTiresAddress ?? ''}
              onChange={(e) => setManualTiresAddress(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div>
        {!frozenOrder && (
          <div className={`filterBar ${dateMissing && !selectedDay && !frozenOrder ? `red` : ''}`}>
            {vehicleOrder?.earliestDelivery && (
              <div className="earliestDelivery">
                <i className="icon-clock" /> {`${t('PROPOSED_DELIVERY')}`}:{' '}
                <strong>{`${printDate(vehicleOrder.earliestDelivery)}`}</strong>
              </div>
            )}
            {dateMissing && !selectedDay && !frozenOrder && (
              <div className="missingDate">
                <i className="icon-warning" /> {t('MISSING_DATE')}
              </div>
            )}
            <div className="select">
              <i className="icon-left" onClick={minusMonth}></i>
              <span>
                {t(monthNames[new Date(date).getMonth()])} {new Date(date).getFullYear()}
              </span>
              <i className="icon-right" onClick={plusMonth}></i>
            </div>
          </div>
        )}
        {!frozenOrder && (
          <Scheduler
            date={date}
            onDateSelected={(day) => selectDay(day)}
            selectedDate={calendarInfoToShow.selectedDay}
            minDate={calendarInfoToShow.minDate}
            maxDate={calendarInfoToShow.maxDate}
            disableSelection={userConfirmation}
          />
        )}
        {combinedView && onDataUpdate && (
          <>
            <div className="checkPro">
              <input type="checkbox" checked={userConfirmation} onChange={handleConfirmationChange} />
              <label style={reminderAcceptMissing ? { color: 'red' } : undefined}>
                <Trans>{t('DELIVERY_ORDER_CHECK_CONFIRMATION')}</Trans>
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  )
  return combinedView ? (
    schedulerContent
  ) : (
    <Modal visible={props.visible} onClose={props.onClose} children={schedulerContent} footer={confirmContainer} />
  )
  // #endregion
}
